import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Plus, Trash2, Refrigerator, ChevronUp, ChevronDown, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { API } from "./admin-utils";

const uid = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

function emptyShelf() {
  return { id: uid("s"), label: "", category_keys: [], capacity: 8 };
}

function emptyFridge(index) {
  return {
    id: uid("f"),
    name: `Fridge ${index + 1}`,
    shelves: [emptyShelf()],
  };
}

export function SmartFridgeTab() {
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [config, setConfig] = useState({ fridges: [] });
  const [restockPassword, setRestockPassword] = useState("112233");
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Initial load: locations + categories
  useEffect(() => {
    let cancelled = false;
    Promise.all([axios.get(`${API}/admin/locations`), axios.get(`${API}/categories`)])
      .then(([locRes, catRes]) => {
        if (cancelled) return;
        setLocations(locRes.data);
        setCategories(catRes.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
        if (locRes.data.length && !selectedSlug) setSelectedSlug(locRes.data[0].slug);
      })
      .catch(() => toast.error("Failed to load locations or categories"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load config when selected location changes
  const loadConfig = useCallback(async (slug) => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/locations/${slug}/fridge-config`);
      setConfig({ fridges: res.data?.fridges || [] });
      setRestockPassword(res.data?.restock_password || "112233");
    } catch {
      toast.error("Failed to load fridge config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSlug) loadConfig(selectedSlug);
  }, [selectedSlug, loadConfig]);

  const categoryByKey = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.key, c])),
    [categories]
  );

  // --- Fridge / shelf editors ---
  const addFridge = () =>
    setConfig((c) => ({ ...c, fridges: [...c.fridges, emptyFridge(c.fridges.length)] }));

  const removeFridge = (fridgeId) =>
    setConfig((c) => ({ ...c, fridges: c.fridges.filter((f) => f.id !== fridgeId) }));

  const updateFridge = (fridgeId, patch) =>
    setConfig((c) => ({
      ...c,
      fridges: c.fridges.map((f) => (f.id === fridgeId ? { ...f, ...patch } : f)),
    }));

  const addShelf = (fridgeId) =>
    updateFridge(fridgeId, {
      shelves: [...(config.fridges.find((f) => f.id === fridgeId)?.shelves || []), emptyShelf()],
    });

  const updateShelf = (fridgeId, shelfId, patch) => {
    const fridge = config.fridges.find((f) => f.id === fridgeId);
    if (!fridge) return;
    updateFridge(fridgeId, {
      shelves: fridge.shelves.map((s) => (s.id === shelfId ? { ...s, ...patch } : s)),
    });
  };

  const removeShelf = (fridgeId, shelfId) => {
    const fridge = config.fridges.find((f) => f.id === fridgeId);
    if (!fridge) return;
    updateFridge(fridgeId, {
      shelves: fridge.shelves.filter((s) => s.id !== shelfId),
    });
  };

  const moveShelf = (fridgeId, shelfId, direction) => {
    const fridge = config.fridges.find((f) => f.id === fridgeId);
    if (!fridge) return;
    const idx = fridge.shelves.findIndex((s) => s.id === shelfId);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= fridge.shelves.length) return;
    const next = [...fridge.shelves];
    [next[idx], next[target]] = [next[target], next[idx]];
    updateFridge(fridgeId, { shelves: next });
  };

  const toggleShelfCategory = (fridgeId, shelfId, key) => {
    const fridge = config.fridges.find((f) => f.id === fridgeId);
    if (!fridge) return;
    const shelf = fridge.shelves.find((s) => s.id === shelfId);
    if (!shelf) return;
    const has = shelf.category_keys.includes(key);
    updateShelf(fridgeId, shelfId, {
      category_keys: has
        ? shelf.category_keys.filter((k) => k !== key)
        : [...shelf.category_keys, key],
    });
  };

  const saveConfig = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/locations/${selectedSlug}/fridge-config`, {
        fridges: config.fridges,
      });
      toast.success("Smart fridge layout saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save layout");
    } finally {
      setSaving(false);
    }
  };

  const saveRestockPassword = async () => {
    if (!selectedSlug) return;
    const pwd = (restockPassword || "").trim();
    if (!/^\d{6}$/.test(pwd)) {
      toast.error("Restock password must be exactly 6 digits");
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/admin/locations/${selectedSlug}/restock-password`, {
        password: pwd,
      });
      toast.success("Restock password updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save password");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading && !config.fridges.length) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#1aa59a]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="smart-fridge-tab">
      {/* Location selector */}
      <Card className="p-4">
        <Label className="text-xs uppercase tracking-wide text-gray-500">Location</Label>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Select value={selectedSlug || ""} onValueChange={setSelectedSlug}>
            <SelectTrigger className="w-full sm:w-72" data-testid="sf-location-select">
              <SelectValue placeholder="Choose a location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.slug} value={loc.slug}>
                  {loc.name} <span className="text-gray-400 ml-2">/{loc.slug}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={saveConfig}
            disabled={saving || !selectedSlug}
            className="bg-[#1aa59a] hover:bg-[#158a80]"
            data-testid="sf-save-config"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save layout"}
          </Button>
        </div>
        {selectedSlug && (
          <p className="text-xs text-gray-500 mt-2">
            Customers see this at <code className="text-[#1aa59a]">/loc/{selectedSlug}/fridge</code>
          </p>
        )}
      </Card>

      {/* Restock password */}
      {selectedSlug && (
        <Card className="p-4" data-testid="sf-restock-password-card">
          <Label className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1">
            <KeyRound className="w-3 h-3" /> Restock View Password
          </Label>
          <p className="text-xs text-gray-500 mt-1 mb-2">
            6-digit code required to access the driver/restock view at{" "}
            <code className="text-[#1aa59a]">/loc/{selectedSlug}/fridge</code>. Default is{" "}
            <code>112233</code>.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={restockPassword}
                onChange={(e) => setRestockPassword(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                inputMode="numeric"
                pattern="\d{6}"
                className="w-40 font-mono tracking-widest pr-9"
                data-testid="sf-restock-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                data-testid="sf-restock-password-toggle"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              onClick={saveRestockPassword}
              disabled={savingPassword}
              variant="outline"
              data-testid="sf-save-restock-password"
            >
              {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save password"}
            </Button>
          </div>
        </Card>
      )}

      {/* Fridges */}
      {config.fridges.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-xl" data-testid="sf-empty-state">
          <Refrigerator className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 mb-3">No fridges configured yet for this location.</p>
          <Button onClick={addFridge} variant="outline" data-testid="sf-add-first-fridge">
            <Plus className="w-4 h-4 mr-1" /> Add first fridge
          </Button>
        </div>
      ) : (
        <>
          {config.fridges.map((fridge, fIdx) => (
            <Card key={fridge.id} className="p-4 border-2" data-testid={`sf-fridge-${fIdx}`}>
              <div className="flex items-center gap-2 mb-3">
                <Refrigerator className="w-5 h-5 text-[#1aa59a]" />
                <Input
                  value={fridge.name}
                  onChange={(e) => updateFridge(fridge.id, { name: e.target.value })}
                  className="font-semibold max-w-xs"
                  data-testid={`sf-fridge-name-${fIdx}`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:bg-red-50 ml-auto"
                  onClick={() => removeFridge(fridge.id)}
                  data-testid={`sf-remove-fridge-${fIdx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {fridge.shelves.map((shelf, sIdx) => (
                  <div
                    key={shelf.id}
                    className="p-3 bg-gray-50 rounded-lg border"
                    data-testid={`sf-shelf-${fIdx}-${sIdx}`}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase">Shelf {sIdx + 1}</span>
                      <Input
                        value={shelf.label}
                        onChange={(e) => updateShelf(fridge.id, shelf.id, { label: e.target.value })}
                        placeholder="Optional label (e.g., 'Top')"
                        className="max-w-xs h-8 text-sm"
                        data-testid={`sf-shelf-label-${fIdx}-${sIdx}`}
                      />
                      <div className="flex items-center gap-1 ml-auto">
                        <Label className="text-xs text-gray-500">Slots</Label>
                        <Input
                          type="number"
                          min={1}
                          max={20}
                          value={shelf.capacity}
                          onChange={(e) =>
                            updateShelf(fridge.id, shelf.id, {
                              capacity: Math.max(1, parseInt(e.target.value, 10) || 1),
                            })
                          }
                          className="w-16 h-8 text-sm"
                          data-testid={`sf-shelf-capacity-${fIdx}-${sIdx}`}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          disabled={sIdx === 0}
                          onClick={() => moveShelf(fridge.id, shelf.id, "up")}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          disabled={sIdx === fridge.shelves.length - 1}
                          onClick={() => moveShelf(fridge.id, shelf.id, "down")}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => removeShelf(fridge.id, shelf.id)}
                          data-testid={`sf-remove-shelf-${fIdx}-${sIdx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {categories.map((cat) => {
                        const active = shelf.category_keys.includes(cat.key);
                        return (
                          <button
                            key={cat.key}
                            type="button"
                            onClick={() => toggleShelfCategory(fridge.id, shelf.id, cat.key)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                              active
                                ? "border-transparent text-white shadow-sm"
                                : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                            }`}
                            style={active ? { backgroundColor: cat.color || "#1aa59a" } : undefined}
                            data-testid={`sf-cat-pill-${fIdx}-${sIdx}-${cat.key}`}
                          >
                            <span className="mr-1">{cat.emoji || "•"}</span>
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>

                    {shelf.category_keys.length > 0 && (
                      <p className="text-xs text-gray-500 mt-2">
                        {shelf.category_keys
                          .map((k) => categoryByKey[k]?.label || k)
                          .join(" + ")}{" "}
                        items at this location will appear here.
                      </p>
                    )}
                  </div>
                ))}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addShelf(fridge.id)}
                  data-testid={`sf-add-shelf-${fIdx}`}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add shelf
                </Button>
              </div>
            </Card>
          ))}

          <Button
            onClick={addFridge}
            variant="outline"
            className="w-full"
            data-testid="sf-add-fridge"
          >
            <Plus className="w-4 h-4 mr-1" /> Add another fridge
          </Button>
        </>
      )}
    </div>
  );
}
